import {Component, OnInit} from '@angular/core';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzTableModule} from 'ng-zorro-antd/table';
import {CommonModule} from '@angular/common';
import {NzModalModule} from 'ng-zorro-antd/modal';
import {FormsModule} from '@angular/forms';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzAutocompleteModule} from 'ng-zorro-antd/auto-complete';
import {KBService} from '../services/kb.service';
import {NzTabsModule} from 'ng-zorro-antd/tabs';
import {MarkdownComponent} from 'ngx-markdown';
import {NzUploadFile, NzUploadModule} from 'ng-zorro-antd/upload';
import {Observable} from 'rxjs';

@Component({
  selector: 'app-knowledge-base',
  standalone: true,
  imports: [
    CommonModule,
    NzCardModule,
    NzButtonModule,
    NzTableModule,
    NzModalModule,
    FormsModule,
    NzFormModule,
    NzTagModule,
    NzInputModule,
    NzAutocompleteModule,
    NzTabsModule,
    MarkdownComponent,
    NzUploadModule
  ],
  templateUrl: './knowledge-base.component.html',
  styleUrl: './knowledge-base.component.less'
})
export class KnowledgeBaseComponent implements OnInit{
  articles: any[] = [];
  loading = false;
  saving = false;

  isModalVisible = false;
  isEditing = false;

  form = { title: '', slug: '', content: '', tags: [] as string[], section: '', subsection: '' };
  tagInput = '';
  editingId: string | null = null;
  contentTabIndex = 0;
  searchText = '';
  filteredArticles: any[] = [];
  existingSections: string[] = [];
  filteredSubsections: string[] = [];

  constructor(private kbService: KBService) {}

  ngOnInit(): void {
    this.loadArticles();
  }

  loadArticles() {
    this.loading = true;
    this.kbService.getArticles().subscribe({
      next: (res) => {
        this.articles = res;
        this.filteredArticles = res;
        this.extractExistingSections(res);
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  extractExistingSections(articles: any[]) {
    const sections = new Set<string>();
    for (const article of articles) {
      if (article.section) {
        sections.add(article.section);
      }
    }
    this.existingSections = Array.from(sections).sort();
  }

  updateFilteredSubsections(): void {
    if (!this.form.section) {
      this.filteredSubsections = [];
      return;
    }

    const subsections = new Set<string>();
    for (const article of this.articles) {
      if (article.section === this.form.section && article.subsection) {
        subsections.add(article.subsection);
      }
    }
    this.filteredSubsections = Array.from(subsections).sort();
  }

  onSectionChange(): void {
    this.form.subsection = '';
    this.updateFilteredSubsections();
  }

  applySearch() {
    const lower = this.searchText.toLowerCase();
    this.filteredArticles = this.articles.filter(article =>
      article.title.toLowerCase().includes(lower)
    );
  }

  showAddModal() {
    this.resetForm();
    this.isModalVisible = true;
  }

  handleCancel() {
    this.isModalVisible = false;
    this.resetForm();
  }

  handleSubmit() {
    this.saving = true;

    const action = this.isEditing
      ? this.kbService.updateArticle(this.editingId!, this.form)
      : this.kbService.createArticle(this.form);

    action.subscribe({
      next: () => {
        this.saving = false;
        this.isModalVisible = false;
        this.loadArticles();
        this.resetForm();
      },
      error: () => (this.saving = false),
    });
  }

  editArticle(article: any) {
    this.form = {
      title: article.title,
      slug: article.slug,
      content: article.content,
      tags: [...article.tags],
      section: article.section ?? '',
      subsection: article.subsection ?? ''
    };

    this.editingId = article._id;
    this.isEditing = true;
    this.isModalVisible = true;
    this.updateFilteredSubsections();
  }

  deleteArticle(id: string) {
    if (!confirm('Delete this article?')) return;
    this.kbService.deleteArticle(id).subscribe(() => this.loadArticles());
  }

  addTag() {
    const tag = this.tagInput.trim();
    if (tag && !this.form.tags.includes(tag)) {
      this.form.tags.push(tag);
    }
    this.tagInput = '';
  }

  removeTag(tag: string) {
    this.form.tags = this.form.tags.filter(t => t !== tag);
  }

  resetForm() {
    this.form = { title: '', slug: '', content: '', tags: [], section: '', subsection: '' };
    this.editingId = null;
    this.isEditing = false;
    this.tagInput = '';
    this.filteredSubsections = [];
  }

  handleImageUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean | Observable<boolean> => {
    const rawFile = file as any; // cast to access native file
    const formData = new FormData();
    formData.append('file', rawFile as File); // assume File is in memory

    this.saving = true;

    this.kbService.uploadImage(formData).subscribe({
      next: (res) => {
        const markdownImage = `\n\n![${file.name}](${res.url})\n\n`;
        this.form.content += markdownImage;
        this.saving = false;
      },
      error: () => {
        this.saving = false;
        alert('Upload failed');
      }
    });

    return false; // Prevent automatic upload
  };

  sort(field: string, order: 'ascend' | 'descend' | null): void {
    if (!order) {
      this.filteredArticles = [...this.articles]; // reset
      return;
    }

    const multiplier = order === 'ascend' ? 1 : -1;
    this.filteredArticles = [...this.filteredArticles].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];

      return (valA > valB ? 1 : valA < valB ? -1 : 0) * multiplier;
    });
  }

  sortMap: { [key: string]: 'ascend' | 'descend' | null } = {
    title: null,
    slug: null,
    section: null,
    subsection: null,
    updatedAt: null
  };

  changeSort(field: string, order: string | null): void {
    // Reset all sort states
    Object.keys(this.sortMap).forEach(key => {
      this.sortMap[key] = null;
    });

    // Handle invalid sort (empty or unsupported)
    if (order !== 'ascend' && order !== 'descend') {
      this.filteredArticles = [...this.articles];
      return;
    }

    // Save valid sort order
    this.sortMap[field] = order;

    const multiplier = order === 'ascend' ? 1 : -1;
    this.filteredArticles.sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      return (valA > valB ? 1 : valA < valB ? -1 : 0) * multiplier;
    });
  }

  downloadMarkdown() {
    const articleCount = this.filteredArticles.length;
    const header = `# Scanafy Knowledge Base Export\n\nTotal Articles: ${articleCount}\n\n---\n`;

    const formatted = this.filteredArticles.map((article, index) => {
      const meta = [
        `> **Title:** ${article.title}`,
        `> **Slug:** ${article.slug}`,
        `> **Section:** ${article.section}`,
        `> **Subsection:** ${article.subsection}`,
        `> **Tags:** ${article.tags.join(', ')}`,
        `> **Updated At:** ${new Date(article.updatedAt).toLocaleString()}`
      ].join('\n');

      return [
        `## ${index + 1}. ${article.title}`,
        '',
        meta,
        '',
        article.content.trim(),
      ].join('\n');
    });

    const markdownContent = [header, ...formatted].join('\n\n---\n\n');

    const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'scanafy_articles.md');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
